/** /launch-policies — cross-tenant Launch_Policy catalog (grid + create/edit
 *  dialog). A launch policy bundles 12 feature flags; a null tenant is a
 *  system/global policy. Flags render as compact centered check indicators so
 *  the row stays dense; most are hidden by default via column visibility. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, CircularProgress, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as launchPoliciesApi from '@/api/launchPolicies';
import { LAUNCH_POLICY_FLAGS, type LaunchPolicyListItem } from '@/api/launchPolicies';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import {
  TenantPicker,
  pickedSentinel,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApiWith } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { launchPolicyActions } from './launchPolicyLinks';
import { LaunchPolicyFormDialog } from './LaunchPolicyFormDialog';

/** Sentinel picker option: system/global policies (`tour_operator IS NULL`). */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System / global (no operator)' };
const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

/** URL `tenant` value → `tour_operator_id` API value: the operator ids
 *  (`12,845`), or the `system` sentinel for null-tenant policies — alone, since
 *  sentinels are exclusive (D3). */
const tenantToApi = tenantIdsToApiWith(['system']);

/** A tri-state flag filter URL value → API. `true`/`false` pass through as
 *  booleans; anything else drops the filter (any). */
const flagToApi = (v: string): boolean | undefined =>
  v === 'true' ? true : v === 'false' ? false : undefined;

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantToApi, tenantScope: 'multi' },
  ...LAUNCH_POLICY_FLAGS.map((f): FilterDef => ({ param: f.key, toApi: flagToApi })),
];

const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Flags all hidden by default (keep the default grid readable); ops reveal the
 *  ones they care about via the columns panel. */
const FLAG_FIELDS = LAUNCH_POLICY_FLAGS.map((f) => f.key);
const DEFAULT_ORDER = [
  'id',
  'name',
  'tenant',
  'in_use_count',
  'description',
  ...FLAG_FIELDS,
  'created_ts',
  'updated_ts',
];
const DEFAULT_HIDDEN = [...FLAG_FIELDS, 'created_ts'];

const fmtDate = (v: string | null): string => (v ? new Date(v).toLocaleDateString() : '');

export function LaunchPoliciesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_launch_policy_manage');

  // Dialog: null policy + open=true → create; a fetched detail → edit.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  const presets = useGridPresets({
    grid: 'launch_policies',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'tenant',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The edit dialog needs the full Detail (atag + flags); fetch it on demand.
  const editQuery = useQuery({
    queryKey: ['launch_policies', 'detail', editId],
    queryFn: () => launchPoliciesApi.retrieve(editId as number),
    enabled: editId !== null,
  });

  const tenantParam = getParam('tenant'); // ids or 'system'
  // The operators the effective tenant filter names (URL > preset > scope): what
  // the create dialog pre-selects / features (D9); `system` names no operator.
  const pageTenants = pageTenantsFrom(tenantParam, useTenantScopeList());
  // The picker's value: those operators, or exactly the System sentinel.
  const tenantValues: TenantPickerValue[] =
    tenantParam?.trim() === 'system' ? [SYSTEM_TENANT] : pageTenants;

  useCreateIntent(() => openCreate());

  const openCreate = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((id: number) => {
    setEditId(id);
    setDialogOpen(true);
  }, []);

  const flagCol = useCallback(
    (key: string, headerName: string): GridColDef<LaunchPolicyListItem> => ({
      field: key,
      headerName,
      width: 70,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
      renderCell: (p) =>
        p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
    }),
    [],
  );

  const columns = useMemo<GridColDef<LaunchPolicyListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) =>
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => openEdit(p.row.id)}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: '#0a1a3f',
                fontWeight: 500,
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              {p.row.name}
            </Box>
          ) : (
            p.row.name
          ),
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        // Sorts by operator code server-side (see backend _ORDER_FIELDS); this
        // is the grid's default sort.
        valueGetter: (_, row) => row.tenant?.name ?? 'System',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty="System" />,
      },
      {
        field: 'in_use_count',
        headerName: 'In use',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        // Sortable server-side (backend _ORDER_FIELDS includes in_use_count).
        valueGetter: (_, row) => row.in_use_count ?? 0,
        description: 'Tours + tenants referencing this policy. 0 = safe to delete.',
        // The count answers "can I delete this?"; the popover answers the next
        // question — which tours. (Tenants defaulting to the policy are part of
        // the count and have no namespace to filter by, hence "tours" only.)
        renderCell: (p) => {
          const n = p.row.in_use_count ?? 0;
          return n === 0 ? (
            0
          ) : (
            <ValueActions actions={launchPolicyActions(p.row, p.row.tenant?.id ?? null)}>
              {n}
            </ValueActions>
          );
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.description ?? '',
      },
      ...LAUNCH_POLICY_FLAGS.map((f) => flagCol(f.key, f.label)),
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 120,
        valueGetter: (_, row) => fmtDate(row.created_ts),
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 120,
        valueGetter: (_, row) => fmtDate(row.updated_ts),
      },
    ],
    [flagCol, canManage, openEdit],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      launchPoliciesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'tenant',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: tenantValues.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenantValues}
            onChange={(next) =>
              setParam(
                'tenant',
                pickedSentinel(next, SYSTEM_EXTRA_OPTIONS)
                  ? 'system'
                  : formatTenantIds(next.map((t) => t.id)),
              )
            }
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
    ...LAUNCH_POLICY_FLAGS.map((f): FilterBarItem => {
      const value = getParam(f.key) ?? 'any';
      return {
        key: f.key,
        priority: 3,
        active: value !== 'any',
        node: (
          <TextField
            select
            size="small"
            label={f.label}
            value={value}
            sx={{ width: 190 }}
            onChange={(e) => setParam(f.key, e.target.value === 'any' ? null : e.target.value)}
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="true">On</MenuItem>
            <MenuItem value="false">Off</MenuItem>
          </TextField>
        ),
      };
    }),
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="launch-policies"
        title="Launch Policies"
        subtitle="Feature-flag bundles that gate what a tour (or tenant default) may do at launch. A system policy has no owning operator."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New launch policy
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<LaunchPolicyListItem>
          queryKey={['launch_policies']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="launch_policies"
          batchActions={[
            batchEditAction('launch_policies', 'launch policy'),
            deleteBatchAction('console_launch_policy_manage'),
          ]}
          defaultSort={{ field: 'tenant', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          emptyMessage="No launch policies yet."
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name',
            exportFileName: 'launch-policies',
            presets: presets.toolbar,
          }}
        />
      )}

      {/* Create: policy=null immediately. Edit: wait for the detail fetch so the
          dialog seeds flags + atags in one shot. */}
      {dialogOpen && (editId === null || editQuery.data) && (
        <LaunchPolicyFormDialog
          open={dialogOpen}
          policy={editId === null ? null : (editQuery.data ?? null)}
          pageTenants={pageTenants}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
          }}
        />
      )}
    </Box>
  );
}
